import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { getQuotes, getServices, getProjects, getTestimonials } from '../services/api';
import { MessageSquare, Wrench, FolderOpen, Star, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    quotes: { total: 0, new: 0 },
    services: 0,
    projects: 0,
    testimonials: 0
  });
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [quotesRes, servicesRes, projectsRes, testimonialsRes] = await Promise.all([
          getQuotes(),
          getServices(),
          getProjects(),
          getTestimonials()
        ]);

        const quotes = quotesRes.data;
        setStats({
          quotes: {
            total: quotes.length,
            new: quotes.filter(q => q.status === 'new').length
          },
          services: servicesRes.data.length,
          projects: projectsRes.data.length,
          testimonials: testimonialsRes.data.length
        });
        setRecentQuotes(quotes.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const statCards = [
    {
      title: 'Quote Requests',
      value: stats.quotes.total,
      subValue: `${stats.quotes.new} new`,
      icon: MessageSquare,
      color: 'bg-blue-500',
      link: '/admin/quotes'
    },
    {
      title: 'Services',
      value: stats.services,
      icon: Wrench,
      color: 'bg-amber-500',
      link: '/admin/services'
    },
    {
      title: 'Projects',
      value: stats.projects,
      icon: FolderOpen,
      color: 'bg-green-500',
      link: '/admin/projects'
    },
    {
      title: 'Testimonials',
      value: stats.testimonials,
      icon: Star,
      color: 'bg-purple-500',
      link: '/admin/testimonials'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-yellow-100 text-yellow-700',
      quoted: 'bg-purple-100 text-purple-700',
      completed: 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600">Welcome to Rooftopper CMS</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.link}>
            <Card className="p-6 bg-white border-2 border-slate-300 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-600">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  {stat.subValue && (
                    <p className="text-sm text-amber-600 mt-1">{stat.subValue}</p>
                  )}
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Quotes */}
      <Card className="p-6 bg-white border-2 border-slate-300">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-slate-900">Recent Quote Requests</h2>
          <Link to="/admin/quotes" className="text-amber-600 hover:text-amber-700 text-sm font-medium">
            View All
          </Link>
        </div>

        {recentQuotes.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No quote requests yet</p>
        ) : (
          <div className="space-y-4">
            {recentQuotes.map((quote) => (
              <div key={quote.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                    <span className="font-semibold text-slate-600">
                      {quote.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{quote.name}</p>
                    <p className="text-sm text-slate-500">{quote.phone} • {quote.service}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                    {quote.status}
                  </span>
                  <span className="text-sm text-slate-400">
                    {new Date(quote.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-3 gap-6">
        <Card className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <TrendingUp className="w-8 h-8 text-amber-400 mb-4" />
          <h3 className="font-semibold mb-2">Manage Content</h3>
          <p className="text-slate-400 text-sm">Update services, projects, and testimonials</p>
        </Card>
        <Card className="p-6 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <Clock className="w-8 h-8 mb-4" />
          <h3 className="font-semibold mb-2">Quick Response</h3>
          <p className="text-amber-100 text-sm">Respond to new quote requests within 24 hours</p>
        </Card>
        <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CheckCircle className="w-8 h-8 mb-4" />
          <h3 className="font-semibold mb-2">Track Progress</h3>
          <p className="text-green-100 text-sm">Mark quotes as contacted, quoted, or completed</p>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
